import { supabase } from '../../config/supabase';

/**
 * Fetch jobs assigned to a specific user (designer and/or estimator), regardless of role.
 */
export const getMyAssignedJobs = async (userId, companyId) => {
    try {
        const [designerResult, estimatorResult] = await Promise.all([
            supabase.from('job_designers').select('job_id').eq('user_id', userId),
            supabase.from('job_estimators').select('job_id').eq('user_id', userId),
        ]);

        const jobIds = [
            ...new Set([
                ...(designerResult.data || []).map(d => d.job_id),
                ...(estimatorResult.data || []).map(e => e.job_id),
            ])
        ];

        if (jobIds.length === 0) return [];

        const { data, error } = await supabase
            .from('jobs')
            .select(`
                *,
                job_quotes(*),
                job_designers(*),
                job_estimators(*),
                job_production(*),
                job_deliveries(*)
            `)
            .eq('company_id', companyId)
            .eq('deleted', false)
            .in('id', jobIds)
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data || [];
    } catch (error) {
        console.error('Error fetching my assigned jobs:', error);
        throw error;
    }
};

/**
 * Check if ALL of the user's assignments on a job are completed.
 *
 * Designers  → checks design_status === 'completed' on the matching job_quote
 *              (by child_job_id for child assignments, or quote_type for parent/standalone).
 *              Falls back to job_designers.is_completed when no quote is found.
 * Estimators → keeps using job_estimators.is_completed (unchanged).
 */
export const isUserAssignmentCompleted = (job, userId) => {
    const userDesigners = job.job_designers?.filter(d => d.user_id === userId) || [];

    // job_estimators can be a single object (one-to-one) or an array depending on
    // how Supabase resolves the relationship — handle both.
    const estimatorList = Array.isArray(job.job_estimators)
        ? job.job_estimators
        : job.job_estimators ? [job.job_estimators] : [];
    const userEstimator = estimatorList.find(e => e.user_id === userId) ?? null;

    const hasAny = userDesigners.length > 0 || !!userEstimator;
    if (!hasAny) return false;

    // Designers: derive completion from the quote's design_status
    const designerDone = userDesigners.length === 0 || userDesigners.every(d => {
        let quote;
        if (d.child_job_id) {
            // Child/department assignment — match by the quote UUID directly
            quote = job.job_quotes?.find(q => q.id === d.child_job_id);
        } else {
            // Type-level assignment — prefer the parent/standalone quote (no child_unique_id)
            quote = job.job_quotes?.find(q => q.quote_type === d.quote_type && !q.child_unique_id)
                ?? job.job_quotes?.find(q => q.quote_type === d.quote_type);
        }

        if (!quote || quote.design_status == null) return !!d.is_completed; // no quote data, fall back
        return quote.design_status === 'completed';
    });

    // Estimators: unchanged — keep using the is_completed flag
    const estimatorDone = !userEstimator || !!userEstimator.is_completed;

    return designerDone && estimatorDone;
};

/**
 * Get all quote assignments for a user on a job (designer + estimator).
 */
export const getUserAssignedQuotes = (job, userId) => {
    const quotes = [];

    job.job_designers?.forEach(designer => {
        if (designer.user_id === userId) {
            quotes.push({
                type: designer.quote_type,
                isCompleted: designer.is_completed,
                isStarted: designer.is_started,
                isEstimator: false,
            });
        }
    });

    const estimatorList2 = Array.isArray(job.job_estimators)
        ? job.job_estimators
        : job.job_estimators ? [job.job_estimators] : [];
    const estimator = estimatorList2.find(e => e.user_id === userId) ?? null;
    if (estimator) {
        quotes.push({
            type: 'estimation',
            isCompleted: estimator.is_completed,
            isStarted: estimator.is_started,
            isEstimator: true,
        });
    }

    return quotes;
};

/**
 * Subscribe to job updates for a company.
 */
export const subscribeToMyJobUpdates = (companyId, callback) => {
    const channel = supabase
        .channel(`my-jobs-changes-${companyId}`)
        .on(
            'postgres_changes',
            { event: '*', schema: 'public', table: 'jobs', filter: `company_id=eq.${companyId}` },
            callback
        )
        .subscribe();

    return channel;
};
